import React, { useEffect } from 'react';
import { createBrowserRouter, RouterProvider, useNavigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { BlueBerryProvider } from '@neshane/blueberry';
import AppRoutes from './routes';
import { queryClient } from './api/queryClient';
import { StepUpProvider } from './modules/SecurityCenter';
import { NotificationsProvider, NotificationsStack } from './common/components/NotificationsStack';
import { PageLoader } from './common/components';
import { useAuth, AuthProvider } from './modules/Authentication';
import { MaintenanceContainer, useMaintenanceStatus } from './modules/Maintenance';
import { FeatureFlagProvider } from './modules/FeatureFlags';
import './App.css';

const faLocales = {
    cancel: 'انصراف',
    close: 'بستن',
    nextMonth: 'ماه بعد',
    previousMonth: 'ماه قبل',
    showOptions: 'نمایش گزینه‌ها',
    openCalendar: 'باز کردن تقویم',
    show: 'نمایش',
    hide: 'پنهان',
    edit: 'ویرایش',
    clearSelection: 'پاک کردن انتخاب',
    selectCurrency: 'انتخاب ارز',
    deleteWithName: ({ name }) => `حذف ${name}`,
};

/**
 * Maintenance Gate Component
 * Blocks regular users when global maintenance mode is enabled.
 * Admins and operators (by role or by /admin & /operator paths) are exempt.
 */
function MaintenanceGate({ children }) {
    const location = useLocation();
    const { user } = useAuth();
    const { enabled, loading } = useMaintenanceStatus();

    const isStaffPath =
        location.pathname.startsWith('/admin') || location.pathname.startsWith('/operator');
    const isStaffUser = user?.role === 'admin' || user?.role === 'operator';

    if (loading) {
        return <PageLoader />;
    }

    if (enabled && !isStaffPath && !isStaffUser) {
        return <MaintenanceContainer />;
    }

    return children;
}

/**
 * Subdomain Detector Component
 * Detects subdomain and redirects to appropriate login page
 */
function SubdomainDetector() {
    const navigate = useNavigate();

    useEffect(() => {
        const hostname = window.location.hostname;
        const subdomain = hostname.split('.')[0];

        // Only redirect if on login page or root
        const currentPath = window.location.pathname;
        if (currentPath === '/' || currentPath === '/login') {
            if (subdomain === 'admin') {
                navigate('/admin/login', { replace: true });
            } else if (subdomain === 'operator') {
                navigate('/operator/login', { replace: true });
            }
        }
    }, [navigate]);

    return (
        <MaintenanceGate>
            <AppRoutes />
        </MaintenanceGate>
    );
}

/**
 * Data router: required for useBlocker/useNavigation hooks used across the app.
 * All declarative routes are matched inside the catch-all route below.
 */
const router = createBrowserRouter([
    { path: '*', element: <SubdomainDetector /> },
]);

function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <FeatureFlagProvider>
                <AuthProvider>
                    <NotificationsProvider>
                        <BlueBerryProvider locale="fa-IR" localesDefinition={{ fallbackLocale: 'fa', locales: { fa: faLocales } }} dir='rtl'>
                            <StepUpProvider>
                                <RouterProvider router={router} />
                            </StepUpProvider>
                            <NotificationsStack />
                        </BlueBerryProvider>
                    </NotificationsProvider>
                </AuthProvider>
            </FeatureFlagProvider>
        </QueryClientProvider>
    );
}

export default App;
